import { apiGateway } from "@/_old/services/api-gateway";
import {
  DeleteMemberParams,
  DeleteMemberResponse,
  DeleteMembersBatchParams,
  DeleteMembersBatchResponse,
  GetMembersParams,
  GetMembersResponse,
  UpdateMemberCostCenterBatchParams,
  UpdateMemberCostCenterBatchResponse,
  UpdateMemberCostCenterParams,
  UpdateMemberCostCenterResponse,
  UpdateMemberPlanAccessBatchParams,
  UpdateMemberPlanAccessBatchResponse,
  UpdateMemberPlanAccessParams,
  UpdateMemberPlanAccessResponse,
  UpdateMemberProfileAccessParams,
  UpdateMemberProfileAccessResponse,
} from "@/company/members/types";

const getMembers = async (
  companyId: number,
  params: GetMembersParams,
  signal?: AbortSignal,
): Promise<GetMembersResponse> => {
  const filters = params.filters?.reduce(
    (acc, filter) => {
      acc[filter.id] = filter.value;
      return acc;
    },
    {} as Record<string, string>,
  );

  const { data, total, count, totalPages } = await apiGateway
    .get(`/companies/${companyId}/members`, {
      params: {
        ...Object.fromEntries(
          Object.entries(params).filter(([key]) => key !== "filters"),
        ),
        ...filters,
      },
      signal,
    })
    .then((res) => res.data)
    .then((res) => {
      return {
        data: res.data,
        total: res.pagination.total,
        count: res.pagination.limit,
        totalPages: res.pagination.totalPages,
      };
    });

  return {
    data,
    total,
    count,
    totalPages,
  } as unknown as GetMembersResponse;
};

const deleteMember = async (
  params: DeleteMemberParams,
  signal?: AbortSignal,
): Promise<DeleteMemberResponse> => {
  const { status, message }: { status: number; message: string } =
    await apiGateway.delete(`/api/v0/companies/members/${params.memberId}`, {
      signal,
    });

  if (status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

const deleteMembersBatch = async (
  params: DeleteMembersBatchParams,
  signal?: AbortSignal,
): Promise<DeleteMembersBatchResponse> => {
  const { status, message }: { status: number; message: string } =
    await apiGateway.delete(`/api/v0/companies/members`, {
      data: {
        emails: params.emails,
      },
      signal,
    });

  if (status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

const updateMemberCostCenter = async (
  params: UpdateMemberCostCenterParams,
  signal?: AbortSignal,
): Promise<UpdateMemberCostCenterResponse> => {
  const {
    status,
    message,
    user: data,
  }: {
    status: number;
    message: string;
    user: UpdateMemberCostCenterResponse;
  } = await apiGateway.patch(
    `/api/v0/companies/groups/members/${params.memberId}`,
    {
      group_id: params.groupId,
    },
    {
      signal,
    },
  );

  if (status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return data;
};

const updateMemberCostCenterBatch = async (
  params: UpdateMemberCostCenterBatchParams,
  signal?: AbortSignal,
): Promise<UpdateMemberCostCenterBatchResponse> => {
  const {
    status,
    message,
  }: {
    status: number;
    message: string;
  } = await apiGateway.patch(
    `/api/v0/companies/groups/batch/members`,
    {
      company_id: params.companyId,
      group_id: params.groupId,
      user_ids: params.memberIds,
    },
    {
      signal,
    },
  );

  if (status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

const updateMemberProfileAccess = async (
  params: UpdateMemberProfileAccessParams,
  signal?: AbortSignal,
): Promise<UpdateMemberProfileAccessResponse> => {
  const {
    statusCode: status,
    message,
  }: {
    statusCode: number;
    message: string;
  } = await apiGateway.patch(`/members/update-permissions-by-role`, params, {
    signal,
  });

  if (status && status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

const updateMemberPlanAccess = async (
  params: UpdateMemberPlanAccessParams,
  signal?: AbortSignal,
): Promise<UpdateMemberPlanAccessResponse> => {
  const {
    status,
    message,
  }: {
    status: number;
    message: string;
  } = await apiGateway.patch(
    `/api/v0/companies/members/${params.memberId}`,
    {
      plan_id: params.planId,
      company_id: params.companyId,
    },
    {
      signal,
    },
  );

  if (status && status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

const updateMemberPlanAccessBatch = async (
  params: UpdateMemberPlanAccessBatchParams,
  signal?: AbortSignal,
): Promise<UpdateMemberPlanAccessBatchResponse> => {
  const {
    status,
    message,
  }: {
    status: number;
    message: string;
  } = await apiGateway.patch(
    `/api/v0/companies/plans/batch/members`,
    {
      plan_id: params.planId,
      company_id: params.companyId,
      user_ids: params.memberIds,
    },
    {
      signal,
    },
  );

  if (status && status !== 200 && status !== 201) {
    throw new Error(message);
  }

  return true;
};

export {
  deleteMember,
  deleteMembersBatch,
  getMembers,
  updateMemberCostCenter,
  updateMemberCostCenterBatch,
  updateMemberPlanAccess,
  updateMemberPlanAccessBatch,
  updateMemberProfileAccess,
};
